import { Fragment, FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Appearance, ProductType } from '@/types';
import { t } from '@/utils/i18n';
import { getParams, isEmbroideryMode, isSmartomat } from '@/utils/params';
import { usePriceFormatter } from '@/hooks/usePrice';
import { getIsOrganic } from '@/model/productType/getIsOrganic';
import { getIsScreenPrintingAppearance } from '@/model/productType/getIsScreenPrintingAppearance';
import { selectProductTypePrice } from '@/redux/common/getters/selectProductTypePrice';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { getPrintTypeIdsForDeliveryETA } from '@/redux/printTypes/getters/getPrintTypeIdsForDeliveryETA';
import { Badge, BadgeTypes } from '@/components/common/Badge';
import { DeliveryEta } from '@/components/common/DeliveryEta';
import { PriceInfo } from '@/components/common/PriceInfo';
import { ProductTypeRating } from '@/components/common/ProductTypeRating';
import { SizeTable } from '@/components/common/SizeTable';
import { Leaf } from '@/components/common/icons/Leaf';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTypes,
} from '@/components/common/modal/modal';
import { PrintTypeSummary } from './PrintTypeSummary';
import './ProductDetailsPanel.scss';

interface ProductDetailsContentProps {
  productType: ProductType;
  appearance: Appearance;
  printTypeIds: string[];
}

export const ProductDetailsContent: FunctionComponent<ProductDetailsContentProps> = ({
  productType,
  appearance,
  printTypeIds,
}) => {
  const isScreenPrintingAppearance = getIsScreenPrintingAppearance(appearance);
  const isOrganic = getIsOrganic(productType);
  const price = useSelector(selectProductTypePrice(productType));
  const priceFormatter = usePriceFormatter();

  function getSizeLabels() {
    return productType.sizes.map((size) => {
      const hasStocks = productType.stockStates.some(
        (stockState) => stockState.size.id === size.id && stockState.available
      );
      return (
        <span
          key={size.id}
          className={cn(
            'product-details-panel__size-label',
            !hasStocks ? 'product-details-panel__size-label--stockout' : null
          )}
        >
          {size.name}
        </span>
      );
    });
  }

  return (
    <div className="product-details-panel">
      <div className="product-details-panel__content">
        <div className="product-details-panel__info">
          <h2>{productType.name}</h2>
          {(isScreenPrintingAppearance || isOrganic) && (
            <div className="product-details-panel__badges">
              {isOrganic && (
                <Badge type={BadgeTypes.Organic}>
                  <Leaf
                    width="1rem"
                    height="1rem"
                    className="product-details-panel__badge-icon--with-text"
                  />
                  {t('sustainable.label')}
                </Badge>
              )}
              {isScreenPrintingAppearance && (
                <Fragment>
                  <Badge type={BadgeTypes.MinAmount}>
                    {t('productDetails.screenprintingBadge')}
                  </Badge>
                  <span className="product-details-panel__min-items">
                    {t('productDetails.minItems')}
                  </span>
                </Fragment>
              )}
            </div>
          )}
          <ProductTypeRating productType={productType} className="product-details-panel__rating" />
          {isSmartomat() && (
            <Fragment>
              <h3 className="product-details-panel__price">{priceFormatter(price)}</h3>
              <PriceInfo
                className="product-details-panel__price-info"
                showVatIncludedString={getParams().locale !== 'us_AU'}
                printPriceTranslationKey={
                  isEmbroideryMode()
                    ? 'embroidery.labels.printPriceOnTop'
                    : 'general.printPriceOnTop'
                }
              />
              <DeliveryEta
                productTypeId={productType.id}
                printTypeIds={printTypeIds}
                className="product-details-panel__delivery-eta"
              />
              {!isScreenPrintingAppearance && <PrintTypeSummary />}
            </Fragment>
          )}
        </div>
        <div className="product-details-panel__sizes">
          <h3>{t('sizePanel.sizeInfo')}</h3>
          <p className="product-details-panel__available-sizes">
            <strong>{t('productDetailsPanel.availableSizesLabel')}</strong>
            {getSizeLabels()}
          </p>
          <SizeTable productType={productType} />
          <p className="product-details-panel__hint">
            <strong>{t('productDetailsPanel.hint.heading')}</strong>{' '}
            {t('productDetailsPanel.hint.text')}
          </p>
        </div>
        <div className="product-details-panel__description">
          <h3>{productType.name}</h3>
          <p
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: productType.description }}
          />
        </div>
      </div>
    </div>
  );
};

export const ProductDetailsPanel: FunctionComponent = () => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch('/product-details'));
  const appearance = useSelector(getCurrentAppearance);
  const productType = useSelector(getProductType);
  const printTypeIds = useSelector(getPrintTypeIdsForDeliveryETA);

  const title = t('productInfoPanel.title');

  return (
    <Modal type={ModalTypes.panel} open={isActive} title={title} onClose={() => navigate('/')}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <ProductDetailsContent
          productType={productType}
          appearance={appearance}
          printTypeIds={printTypeIds}
        />
      </ModalContent>
    </Modal>
  );
};
